/** @jsxImportSource preact */
import { useEffect, useState } from "preact/hooks";

import type { CartProductDetail } from "../cart/cart";
import { anyAvailable } from "../cart/selection";
import * as copy from "../paraglide/messages.js";
import type { CartOpeners } from "./cart/cart-controller";
import { formVars } from "./form/form-styles";
import { PlusIcon } from "./icons";
import type { ResolvedProduct } from "./message";
import { leavesPage } from "./navigation";
import { SR_ONLY } from "./sr-only";
import type { WidgetTokens } from "./theme";

/** 点了卡片却一直没换走（店面脚本拦下了跳转），这么久后撤掉「正在打开」。 */
const OPENING_RESET_MS = 3000;

/**
 * 商品卡：白色小圆角卡，图片区是内圆角块，右下角悬一颗白色圆形「+」加购，下面是标题和价格。
 * 多张卡在消息里是横向滚动的一排，所以宽度固定、flex:none，不能被挤扁。
 *
 * 一条消息会有好几张商品卡，每张自带一份样式，所以这里只放自己的规则、颜色和圆角只读根节点上的
 * --gn-form-* 变量，不再带整套表单样式。
 * 整张卡点了都进商品页：链接的 ::after 铺满卡片。「+」不能塞进链接里（链接里不能嵌按钮），
 * 所以和链接并排、靠卡片宽度算好的位置盖在图片右下角，叠在铺满的那层上面，点它只开加购面板、不跳转。
 */
export const PRODUCT_CARD_STYLES = `
.gn-product-card { --gn-pc-w:150px; --gn-pc-pad:10px; --gn-pc-btn:36px; --gn-pc-inset:8px; position:relative; box-sizing:border-box; flex:none; width:var(--gn-pc-w); display:flex; flex-direction:column; gap:10px; padding:var(--gn-pc-pad) var(--gn-pc-pad) 12px; border:0; border-radius:var(--gn-form-r-tile); background:var(--gn-form-tray); color:var(--gn-form-ink); font-size:13px; line-height:1.3; text-align:left; transition:box-shadow .15s; }
.gn-product-card[data-opening] { box-shadow:0 0 0 2px var(--gn-form-ring); }
.gn-pc-link { display:flex; flex-direction:column; gap:10px; color:inherit; text-decoration:none; }
.gn-pc-link::after { content:""; position:absolute; inset:0; border-radius:inherit; }
.gn-pc-link:focus-visible { outline:2px solid var(--gn-form-ring); outline-offset:3px; border-radius:var(--gn-form-r-cell); }
.gn-pc-media { aspect-ratio:1; overflow:hidden; border-radius:var(--gn-form-r-cell); background:var(--gn-form-sheet); }
.gn-pc-img { display:block; width:100%; height:100%; object-fit:cover; }
.gn-pc-info { display:flex; flex-direction:column; gap:3px; padding:0 2px; }
.gn-pc-title { display:-webkit-box; overflow:hidden; -webkit-box-orient:vertical; -webkit-line-clamp:2; font-size:13px; font-weight:600; line-height:1.3; }
@media (hover: hover) { .gn-pc-link:hover .gn-pc-title { text-decoration:underline; text-underline-offset:3px; } }
.gn-pc-price { font-size:15px; font-weight:800; letter-spacing:-.02em; line-height:20px; font-variant-numeric:tabular-nums; }
.gn-pc-price[data-opening] { font-size:12px; font-weight:600; letter-spacing:0; color:var(--gn-form-quiet); }
.gn-pc-add { position:absolute; z-index:1; right:calc(var(--gn-pc-pad) + var(--gn-pc-inset)); top:calc(var(--gn-pc-w) - var(--gn-pc-pad) - var(--gn-pc-inset) - var(--gn-pc-btn)); box-sizing:border-box; display:flex; align-items:center; justify-content:center; width:var(--gn-pc-btn); height:var(--gn-pc-btn); padding:0; border:0; border-radius:var(--gn-form-r-pill); background:var(--gn-form-raised, var(--gn-form-bg)); box-shadow:var(--gn-form-shadow-pill), 0 2px 8px color-mix(in srgb, var(--gn-form-ink) 14%, transparent); color:var(--gn-form-ink); cursor:pointer; transition:background .15s, color .15s, filter .15s; }
.gn-pc-add:hover:not(:disabled) { filter:brightness(.96); }
.gn-pc-add:focus-visible { outline:2px solid var(--gn-form-ring); outline-offset:2px; }
.gn-pc-add:disabled { cursor:default; opacity:.55; }
.gn-pc-note { margin:0; padding:0 2px; font-size:12px; line-height:1.4; color:var(--gn-form-quiet); }
.gn-pc-note[data-capsule] { align-self:flex-start; box-sizing:border-box; display:inline-flex; align-items:center; min-height:26px; padding:0 10px; border-radius:var(--gn-form-r-pill); background:var(--gn-form-sheet); font-weight:700; line-height:1.2; }
@media (prefers-reduced-motion: reduce) { .gn-product-card, .gn-pc-add { transition:none; } }
`;

interface ProductCardProps {
  product: ResolvedProduct;
  tokens: WidgetTokens;
  /** 聊天面板的加购入口：先读商品能不能买，「+」点了在面板里选变体和数量。
   *  没有店面（后台回放、Playground）就没有它，卡上不摆加购。 */
  cart?: CartOpeners;
  linksInNewTab?: boolean;
  /** 入场动画：跟着刚流完的回复出来时才有，时刻由消息按卡片位置排。 */
  animation?: string;
}

export function ProductCard({
  product,
  tokens: t,
  cart,
  linksInNewTab = false,
  animation,
}: ProductCardProps) {
  const [imageFailed, setImageFailed] = useState(false);
  // 设计稿 12a：点了卡片、新页面还没出来时，卡片外圈亮起墨色环、价格旁写「正在打开」。
  const [opening, setOpening] = useState(false);
  useEffect(() => {
    if (!opening) return;
    const reset = () => {
      setOpening(false);
    };
    const timer = setTimeout(reset, OPENING_RESET_MS);
    // 从往返缓存回来时页面原样恢复，描边还亮着。
    window.addEventListener("pageshow", reset);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("pageshow", reset);
    };
  }, [opening]);

  return (
    <div
      class="gn-product-card"
      data-opening={opening || undefined}
      style={{ ...formVars(t), animation }}
    >
      <style>{PRODUCT_CARD_STYLES}</style>
      <a
        class="gn-pc-link"
        href={product.url ?? "#"}
        onClick={(event) => {
          if (!linksInNewTab && leavesPage(event)) setOpening(true);
        }}
        rel="noreferrer"
        target={linksInNewTab ? "_blank" : undefined}
      >
        <div class="gn-pc-media">
          {/* 图取不回来就收掉，只留底色方块，别在买家眼前摆个破图标。
              别改回给 img 设 hidden：样式里的 display:block 会盖过它，破图标照样露出来。 */}
          {product.hasImage && !imageFailed && (
            <img
              alt=""
              class="gn-pc-img"
              onError={() => {
                setImageFailed(true);
              }}
              src={product.imageUrl}
            />
          )}
        </div>
        <div class="gn-pc-info">
          <div class="gn-pc-title">{product.title}</div>
          <div class="gn-pc-price" data-opening={opening || undefined}>
            {opening
              ? copy.widget_product_opening({ price: product.price })
              : product.price}
          </div>
        </div>
      </a>
      {cart !== undefined && product.url !== undefined && (
        <Purchase cart={cart} product={product} productUrl={product.url} />
      )}
    </div>
  );
}

/**
 * 卡上的加购入口：先去店面读这件商品能不能买。能买就摆「+」，点了开加购面板选变体和数量；
 * 全部售罄、只能订阅（订阅方案要在商品页选）或读不到时，在卡片底部说明，不摆「+」。
 * 加购结果不画在卡上，由面板确认后在聊天里插一条回执。
 */
function Purchase({
  cart,
  product,
  productUrl,
}: {
  cart: CartOpeners;
  product: ResolvedProduct;
  productUrl: string;
}) {
  const [detail, setDetail] = useState<CartProductDetail | "failed">();
  const { readProduct } = cart;
  useEffect(() => {
    let disposed = false;
    void readProduct(productUrl).then((result) => {
      if (!disposed) setDetail(result.isOk() ? result.value : "failed");
    });
    return () => {
      disposed = true;
    };
  }, [productUrl, readProduct]);

  if (detail === undefined) return null;
  if (detail === "failed")
    return (
      <div class="gn-pc-note" role="status">
        {copy.widget_product_load_failed()}
      </div>
    );
  const capsule = detail.sellingPlanOnly
    ? copy.widget_product_subscription()
    : anyAvailable(detail)
      ? undefined
      : copy.widget_product_sold_out();
  if (capsule !== undefined)
    return (
      <div class="gn-pc-note" data-capsule="" role="status">
        {capsule}
      </div>
    );
  return (
    <button
      aria-haspopup="dialog"
      class="gn-pc-add"
      disabled={cart.busy}
      onClick={(event) => {
        cart.openAdd(product, detail, event.currentTarget);
      }}
      type="button"
    >
      <PlusIcon size={18} strokeWidth={2.4} />
      {/* 可见的只有图标，名字放进读屏专用文本。 */}
      <span style={SR_ONLY}>{copy.widget_product_add()}</span>
    </button>
  );
}
